/**
 * Juridiction (« marché » côté code : fr / eu) — indépendante de la langue d'UI.
 *
 * La juridiction choisit les données interrogées (droit français, droit de l'UE)
 * et la page d'accueil ; la langue (i18n) ne change que les textes.
 * Un avocat allemand peut consulter la jurisprudence française avec une interface en DE.
 *
 * Résolution :
 *   1. ?market= dans l'URL (mémorisé)
 *   2. choix mémorisé (localStorage)
 *   3. navigateur : région de navigator.languages (fr-FR → fr, de-DE → eu),
 *      à défaut la langue (fr → fr, de / en → eu)
 *   4. window.__DEFAULT_MARKET__ (env DEFAULT_MARKET côté serveur) > 'fr'
 *
 * `installMarketFetch()` ajoute l'en-tête X-SL-Market à chaque appel same-origin
 * vers /api/… : le backend (chainlit/corpus) en déduit les collections à interroger.
 */
import { useSyncExternalStore } from 'react';

export const MARKETS = ['fr', 'eu'] as const;
export type Market = (typeof MARKETS)[number];

const STORAGE_KEY = 'sl_market';
const HEADER = 'X-SL-Market';
const EVENT = 'sl:market';

/** Page d'accueil de chaque juridiction. */
export const MARKET_HOME: Record<Market, string> = {
  fr: '/',
  eu: '/jurisprudence/cjue'
};

// Régions → juridiction (le reste de l'Europe lit le droit de l'UE)
const FR_REGIONS = new Set([
  'FR',
  'MC',
  'GP',
  'MQ',
  'GF',
  'RE',
  'YT',
  'PM',
  'NC',
  'PF'
]);
const EU_REGIONS = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
  'CH',
  'NO',
  'IS',
  'LI'
]);
const LANG_MARKET: Record<string, Market> = { fr: 'fr', de: 'eu', en: 'eu' };

function isMarket(v: unknown): v is Market {
  return typeof v === 'string' && (MARKETS as readonly string[]).includes(v);
}

export function isBot(): boolean {
  return /bot|crawl|spider|slurp|facebookexternalhit|embedly|lighthouse|prerender|headless/i.test(
    navigator.userAgent || ''
  );
}

export function getDefaultMarket(): Market {
  const m = (window.__DEFAULT_MARKET__ || '').toLowerCase();
  return isMarket(m) ? m : 'fr';
}

/** Juridiction déduite du navigateur, ou null si rien d'exploitable. */
export function detectBrowserMarket(): Market | null {
  const tags = (
    navigator.languages?.length ? navigator.languages : [navigator.language]
  ).filter(Boolean);
  for (const tag of tags) {
    const region = tag.split('-')[1]?.toUpperCase();
    if (!region) continue;
    if (FR_REGIONS.has(region)) return 'fr';
    if (EU_REGIONS.has(region)) return 'eu';
  }
  for (const tag of tags) {
    const m = LANG_MARKET[tag.slice(0, 2).toLowerCase()];
    if (m) return m;
  }
  return null;
}

function storedMarket(): Market | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('market');
    if (isMarket(fromUrl)) {
      localStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isMarket(stored)) return stored;
  } catch {
    // localStorage indisponible (navigation privée stricte…)
  }
  return null;
}

/** true si l'utilisateur a déjà choisi (ou reçu via ?market=) une juridiction. */
export function hasChosenMarket(): boolean {
  return storedMarket() !== null;
}

/** Juridiction préférée de l'utilisateur, hors contrainte de route. */
export function getPreferredMarket(): Market {
  return storedMarket() ?? detectBrowserMarket() ?? getDefaultMarket();
}

/** Juridiction effective de la page courante. */
export function getMarket(): Market {
  return getPreferredMarket();
}

export function setMarket(market: Market): void {
  try {
    localStorage.setItem(STORAGE_KEY, market);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: market }));
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', cb);
  };
}

/** Juridiction préférée, re-rendue à chaque changement (switcher, autre onglet). */
export function usePreferredMarket(): Market {
  return useSyncExternalStore(subscribe, getPreferredMarket, getDefaultMarket);
}

function isApiUrl(input: RequestInfo | URL): boolean {
  const raw =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.href
      : input.url;
  try {
    const url = new URL(raw, window.location.origin);
    return (
      url.origin === window.location.origin && url.pathname.startsWith('/api/')
    );
  } catch {
    return false;
  }
}

let installed = false;

export function installMarketFetch(): void {
  if (installed || typeof window.fetch !== 'function') return;
  installed = true;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isApiUrl(input)) return nativeFetch(input, init);

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    if (!headers.has(HEADER)) headers.set(HEADER, getMarket());
    return nativeFetch(input, { ...init, headers });
  };
}
